import React from 'react';
import styles from './MessagesTile.module.scss';
import classNames from 'classnames';
import { Conversation } from './types/Conversation';
import { MessagesTileResult } from './types/MessagesTileResult';
import withAbortRequest, { AbortRequestPropsType } from '../../../core/hoc/withAbortRequest';
import { InfoMessageService, TranslationService } from '../../../core/services';
import { Alert, Icon, Loader, ModuleContainer } from '../../../core/components';
import { Message } from './types/Message';
import ApiProvider from '../../../core/api/ApiProvider';
import { useHistory } from 'react-router-dom';
import { AppContext } from '../../../AppContext';
import { useQuery } from 'react-query';
import { ActionStatusType } from '../../../core/types/ActionStatusType';

const MessagesTile = (props: AbortRequestPropsType) => {
  const context = React.useContext(AppContext);
  const history = useHistory();

  const unreadMessages = useQuery<ActionStatusType<MessagesTileResult>>(
    'unreadMessages',
    () =>
      ApiProvider.get<ActionStatusType<MessagesTileResult>>(
        'MessagesTileModule/GetMessagesTile',
        props.cancelTokenSource.token
      ),
    {
      enabled: !context.isHideMypageCases,
      refetchOnWindowFocus: false,
      onError: () => {
        InfoMessageService.error(TranslationService.translate('ErrorSthWentWrong'));
      },
    }
  );

  const onClick = (conversation?: Conversation) => {
    if (unreadMessages.isFetching) {
      return;
    }

    const url = '/Messages' + (conversation ? `/${conversation.messageId}` : '');
    history.push(url);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLSpanElement>) => {
    if (event.key === 'Enter') {
      onClick();
    }
  };

  const getMessageContent = (message: Message) => {
    return message.noteText.slice(0, 35);
  };

  return (
    <>
      {unreadMessages.data?.data.isEnabled && (
        <ModuleContainer classNames={styles.wrapper}>
          <ModuleContainer.Header
            id="MessagesLabel"
            classNames={styles.header}
            title={TranslationService.translate('MessagesTab')}
            headingLevel="h2"
          >
            <span tabIndex={0} onKeyDown={handleKeyDown}>
              <Icon
                iconVariant={Icon.Variants.FontAwesome}
                className={styles.headerIcon}
                isPointer={true}
                icon="fas fa-arrow-right cursor cursor--pointer"
                onClick={() => onClick()}
              />
            </span>
          </ModuleContainer.Header>

          <ModuleContainer.Body
            variant={
              unreadMessages.isFetching || unreadMessages.data?.data.conversations.length === 0
                ? ModuleContainer.Body.BodyVariant.Primary
                : ModuleContainer.Body.BodyVariant.Secondary
            }
          >
            {unreadMessages.isFetching ? (
              <Loader opacity={0.4} />
            ) : (
              <>
                {!unreadMessages.isFetching &&
                  unreadMessages.data?.data.conversations.length === 0 && (
                    <Alert
                      className="mb-0"
                      variant={Alert.Variants.Info}
                      text={TranslationService.translate('NoNewMsgs')}
                    />
                  )}
                <ul className={styles.list} id="MessagesList">
                  {unreadMessages.data?.data.conversations.map((conversation: any, index: any) => (
                    <li
                      className={classNames(
                        styles.listItem,
                        !conversation.isRead && styles.msgUnread
                      )}
                      key={index}
                      onClick={() => onClick(conversation)}
                    >
                      <div>
                        <div className={styles.msgCase}>
                          {TranslationService.translate('CaseNr')}:{' '}
                          {conversation.caseDetails.caseNumber}
                        </div>
                        <div className={styles.msgText}>
                          {getMessageContent(conversation.notes[conversation.notes.length - 1])}
                        </div>
                      </div>
                      <div className={styles.msgDate}>{conversation.sendDateFormatted}</div>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </ModuleContainer.Body>
        </ModuleContainer>
      )}
    </>
  );
};

export default withAbortRequest(MessagesTile);
